import React, { useState } from 'react';
import Radium from 'radium';
import PropTypes from 'prop-types';
import { Editor, Transforms } from 'slate';
import {
  BiBold as BoldIcon,
  BiItalic as ItalicIcon,
  BiUnderline as UnderlinedIcon,
  BiStrikethrough as StrikethroughIcon,
  BiLink as LinkIcon,
  BiUndo as UndoIcon,
  BiRedo as RedoIcon
} from 'react-icons/bi';
import { ReactComponent as ClickupCreateIcon } from 'images/ic_clickup_integration.svg';
import {
  MdFormatListBulleted as BulletedListIcon,
  MdFormatListNumbered as NumberedListIcon
} from 'react-icons/md';
import { ReactComponent as ChecklistIcon } from 'images/ic_editor_checklist.svg';
import EditorToolbarButton from 'components/Editor/EditorToolbarButton';
import Tokens from 'styles/tokens';
import {
  TYPE_HEADER1,
  TYPE_HEADER2,
  TYPE_HEADER3,
  TYPE_PARAGRAPH
} from 'components/Notes/Slate/Plugins/Types';
import { useSelector } from 'react-redux';
import {
  UNDERLINE,
  BOLD,
  ITALIC,
  STRIKETHROUGH
} from 'components/Notes/Slate/Plugins/withMarks';
import { mobile } from 'helpers/deviceCutoffs';
import LinkEditPopover from './Elements/Link/EditPopover';
import isNodeTypeIn from './Plugins/Queries/isNodeTypeIn';
import {
  TYPE_CHECKLIST,
  TYPE_ORDERED_LIST,
  TYPE_UNORDERED_LIST
} from './Plugins/List/types';
import { isBlockActive } from './Plugins/List/Queries/getActiveListType';

const Toolbar = ({
  editor,
  isPrivateNote,
  roundedToolbar,
  handleClickupToolbarButton,
  isCollab
}) => {
  const [shouldShowLinkInput, setShouldShowLinkInput] = useState(false);
  const [selectionRange, setSelectionRange] = useState(null);

  const [linkInput, setLinkInput] = useState('');
  const [titleInput, setTitleInput] = useState('');

  const clickupIntegration = useSelector(
    state => state.user.clickupIntegration
  );

  const showLinkInput = () => {
    setShouldShowLinkInput(true);
    setSelectionRange(editor.selection);
  };

  const hideLinkInput = () => {
    Transforms.select(editor, selectionRange);
    setShouldShowLinkInput(false);
    setSelectionRange(null);
    setLinkInput('');
    setTitleInput('');
  };

  const toggleHeaderType = headerType => () => {
    if (isNodeTypeIn(editor, headerType)) {
      editor.setType(TYPE_PARAGRAPH);
    } else {
      editor.setType(headerType);
    }
  };

  const handleCreateLink = () => {
    editor.insertLink(linkInput, titleInput);
    hideLinkInput();
  };

  return (
    <React.Fragment>
      <div
        style={{
          ...styles.toolbarContainer,
          ...(roundedToolbar ? styles.roundedToolbar : {}),
          ...(isPrivateNote && styles.privateNoteToolbar)
        }}
        key='toolbar'
      >
        <EditorToolbarButton
          key={TYPE_HEADER1}
          text='H1'
          active={isNodeTypeIn(editor, TYPE_HEADER1)}
          onClick={toggleHeaderType(TYPE_HEADER1)}
          tooltip='Heading 1'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={TYPE_HEADER2}
          text='H2'
          active={isNodeTypeIn(editor, TYPE_HEADER2)}
          onClick={toggleHeaderType(TYPE_HEADER2)}
          tooltip='Heading 2'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={TYPE_HEADER3}
          text='H3'
          active={isNodeTypeIn(editor, TYPE_HEADER3)}
          onClick={toggleHeaderType(TYPE_HEADER3)}
          tooltip='Heading 3'
          isPrivateNote={isPrivateNote}
        />
        <hr
          style={{
            ...styles.separator,
            ...(isPrivateNote && styles.privateNoteSeparator)
          }}
        />
        <div style={{ position: 'relative' }}>
          <EditorToolbarButton
            Icon={LinkIcon}
            onClick={showLinkInput}
            tooltip='Insert link'
            disabled={editor.selection === null}
            isPrivateNote={isPrivateNote}
            active={shouldShowLinkInput}
          />
          {shouldShowLinkInput && (
            <LinkEditPopover
              onOutsideClick={hideLinkInput}
              handleLinkChange={value => setLinkInput(value)}
              handleTitleChange={value => setTitleInput(value)}
              isCreate
              onClickCreate={handleCreateLink}
              style={{ marginTop: Tokens.spacing.one, position: 'fixed' }}
              url={linkInput}
              title={titleInput}
              isTextSelectedForLink={
                !!(editor?.selection && Editor.string(editor, editor.selection))
              }
            />
          )}
        </div>
        <EditorToolbarButton
          key={BOLD}
          Icon={BoldIcon}
          active={editor.isMarkActive(BOLD)}
          onClick={() => editor.toggleMark(BOLD)}
          tooltip='Bold'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={ITALIC}
          Icon={ItalicIcon}
          active={editor.isMarkActive(ITALIC)}
          onClick={() => editor.toggleMark(ITALIC)}
          tooltip='Italic'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={UNDERLINE}
          Icon={UnderlinedIcon}
          active={editor.isMarkActive(UNDERLINE)}
          onClick={() => editor.toggleMark(UNDERLINE)}
          tooltip='Underline'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={STRIKETHROUGH}
          Icon={StrikethroughIcon}
          active={editor.isMarkActive(STRIKETHROUGH)}
          onClick={() => editor.toggleMark(STRIKETHROUGH)}
          tooltip='Strikethrough'
          isPrivateNote={isPrivateNote}
        />
        <hr
          style={{
            ...styles.separator,
            ...(isPrivateNote && styles.privateNoteSeparator)
          }}
        />
        <EditorToolbarButton
          key={TYPE_ORDERED_LIST}
          Icon={NumberedListIcon}
          iconStyles={{ fontSize: '1.9em' }}
          active={isBlockActive(editor, TYPE_ORDERED_LIST)}
          onClick={() => editor.toggleList(TYPE_ORDERED_LIST)}
          tooltip='Numbered list'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={TYPE_UNORDERED_LIST}
          Icon={BulletedListIcon}
          iconStyles={{ fontSize: '1.9em' }}
          active={isBlockActive(editor, TYPE_UNORDERED_LIST)}
          onClick={() => editor.toggleList(TYPE_UNORDERED_LIST)}
          tooltip='Bulleted list'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key={TYPE_CHECKLIST}
          Icon={ChecklistIcon}
          active={isBlockActive(editor, TYPE_CHECKLIST)}
          onClick={() => editor.toggleList(TYPE_CHECKLIST)}
          tooltip='Checklist'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key='undo-button'
          Icon={UndoIcon}
          onClick={() => editor.undo()}
          tooltip='Undo Changes'
          isPrivateNote={isPrivateNote}
        />
        <EditorToolbarButton
          key='redo-button'
          Icon={RedoIcon}
          onClick={() => editor.redo()}
          tooltip='Redo Changes'
          isPrivateNote={isPrivateNote}
        />
        {clickupIntegration && isCollab && (
          <EditorToolbarButton
            key='create-clickup-task-button'
            Icon={ClickupCreateIcon}
            onClick={handleClickupToolbarButton}
            tooltip={
              !(editor?.selection && Editor.string(editor, editor.selection))
                ? 'Select text first to Create ClickUp Task'
                : 'Create Clickup Task'
            }
            disabled={
              !(editor?.selection && Editor.string(editor, editor.selection))
            }
            isPrivateNote={isPrivateNote}
            iconStyles={{
              stroke: 'none',
              width: Tokens.spacing.onehalf,
              height: Tokens.spacing.onehalf
            }}
          />
        )}
      </div>
    </React.Fragment>
  );
};

Toolbar.propTypes = {
  editor: PropTypes.object.isRequired,
  isPrivateNote: PropTypes.bool,
  roundedToolbar: PropTypes.bool,
  handleClickupToolbarButton: PropTypes.func,
  isCollab: PropTypes.bool
};

Toolbar.defaultProps = {
  isPrivateNote: false,
  roundedToolbar: false
};

const styles = {
  separator: {
    backgroundColor: Tokens.colors.gainsboro,
    width: '1px',
    border: '0',
    height: Tokens.spacing.three,
    margin: `0 ${Tokens.spacing.half}`
  },
  privateNoteSeparator: {
    backgroundColor: Tokens.colors.yellow
  },
  toolbarContainer: {
    boxSizing: 'border-box',
    position: 'sticky',
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${Tokens.colors.gainsboro}`,
    overflow: 'hidden',
    padding: `${Tokens.spacing.half} ${Tokens.spacing.two}`,
    backgroundColor: Tokens.colors.white,
    top: 0,
    width: '100%',
    left: 0,
    zIndex: 2,
    gap: Tokens.spacing.half
  },
  roundedToolbar: {
    borderTopLeftRadius: Tokens.spacing.borderRadius,
    borderTopRightRadius: Tokens.spacing.borderRadius
  },
  inputContainer: {
    position: 'sticky',
    width: '220px',
    left: Tokens.spacing.five,
    top: Tokens.spacing.five,
    zIndex: 2,
    ...Tokens.shadows.medium,
    borderRadius: Tokens.spacing.borderRadius,
    border: Tokens.borders.standard,
    backgroundColor: Tokens.colors.whiteSmoke,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center'
  },
  privateNoteToolbar: {
    borderBottom: `1px solid ${Tokens.colors.yellow}`,
    borderTop: `1px solid ${Tokens.colors.yellow}`,
    backgroundColor: Tokens.colors.yellowLightest,
    marginTop: Tokens.spacing.one,

    [`@media (max-width: ${mobile}px)`]: {
      overflowX: 'auto'
    }
  }
};

export default Radium(Toolbar);
